import { useFormContext } from "react-hook-form";
import { useEffect } from "react";
import { useGetLocation } from "../../repositories/licences-repository";
import { regions } from "@/constants/regions";
import useAuth from "@/providers/auth/useAuth";

import SelectInput from "@/components/form-controls/SelectInput";
import TextInput from "@/components/form-controls/TextInput";
import PhoneNumberInput from "@/components/form-controls/PhoneInput";



export default function BusinessForm() {
    const { register, formState: { errors }, clearErrors, control, watch, setValue } = useFormContext()
    const { user } = useAuth();

    const { zones } = useGetLocation({});

    const getZones = zones?.results?.map((el: { name: any; }) => ({ label: el.name, value: el.name }));

    const getRegions = regions.map((el) => ({ label: el.name, value: el.name }));

    const getDistricts = (name?: string) => {
        let region = regions.find(el => el.name === name);
        if (region) return region.districts.map((el) => ({ label: el.name, value: el.name }));
        return [];
    };

    const getMunicipals = (regionName?: string, districtName?: string) => {
        let region = regions.find(el => el.name === regionName);
        if (region) {
            let district = region.districts.find(el => el.name === districtName);
            if (district) {
                return district.municipals.map((el) => ({ label: el, value: el }));
            }
        }
        return [];
    };

    const selectedRegion = watch("region");
    const selectedDistrict = watch("district");

    // Pre-fill user data from API
    useEffect(() => {
        if (user) {
            // Pre-fill name from user info
            if (user.firstName && !watch("firstName")) {
                setValue("firstName", user.firstName);
            }
            if (user.lastName && !watch("lastName")) {
                setValue("lastName", user.lastName);
            }
            // Pre-fill business name from user info
            if (user.business?.businessName && !watch("businessName")) {
                setValue("businessName", user.business.businessName);
            }
            // Pre-fill TIN from user info
            if (user.business?.businessTinNumber && !watch("tin")) {
                setValue("tin", user.business.businessTinNumber);
            }
        }
    }, [user, setValue, watch]);

    const handleInputBlur = (fieldName: string) => {
        if (errors[fieldName]) {
            clearErrors(fieldName);
        }
    }

    return (
        <div className="w-full">
            <h1 className="text-lg font-bold">Business Details</h1>
            <p className="text-xs text-[#041528]">Provide accurate information about your business as it appears on official registration documents.</p>
            <div className="mt-5 grid grid-cols-1 gap-3 pb-5">
                <div className="flex items-center gap-3">
                    <TextInput
                        label="First Name"
                        placeholder="Enter your first name"
                        onBlur={() => handleInputBlur("firstName")}
                        error={errors.firstName?.message?.toString()}
                        register={register("firstName", {
                            required: "First name is required"
                        })}
                        className="placeholder:font-extralight w-full"
                        required
                    />

                    <TextInput
                        label="Last Name"
                        placeholder="Enter your last name"
                        onBlur={() => handleInputBlur("lastName")}
                        error={errors.lastName?.message?.toString()}
                        register={register("lastName", {
                            required: "Last name is required"
                        })}
                        className="placeholder:font-extralight w-full"
                        required
                    />
                </div>

                <div className="flex items-center gap-3">
                    <TextInput
                        label="Project Name"
                        placeholder="What is your project name?"
                        onBlur={() => handleInputBlur("projectName")}
                        error={errors.projectName?.message?.toString()}
                        register={register("projectName", {
                            required: "Project name is required"
                        })}
                        className="placeholder:font-extralight w-full"
                        required
                    />

                    <TextInput
                        label="Physical Location"
                        placeholder="Where is your business located?"
                        onBlur={() => handleInputBlur("physicalLocation")}
                        error={errors.physicalLocation?.message?.toString()}
                        register={register("physicalLocation")}
                        className="placeholder:font-extralight w-full"
                    />
                </div>

                <TextInput
                    label="Name under which business is carried on (to be licensed)"
                    placeholder="Provide the name your business"
                    onBlur={() => handleInputBlur("businessName")}
                    error={errors.businessName?.message?.toString()}
                    register={register("businessName")}
                    className="placeholder:font-extralight col-span-2"
                />

                <TextInput
                    label="TIN Number"
                    placeholder="Enter your TIN number"
                    onBlur={() => handleInputBlur("tin")}
                    error={errors.tin?.message?.toString()}
                    register={register("tin")}
                    className="placeholder:font-extralight col-span-2"
                />

                <div className="flex items-center w-full text-sm gap-3 ">
                    <span className="text-nowrap text-black font-semibold">Principal Business Location</span>
                    <span className="border border-[#E3E7E8] w-full h-[0.5px]" />
                </div>

                <div className="col-span-2 grid grid-cols-2 gap-3">

                        <SelectInput
                            label="Zone"
                            displayName="label"
                            valueName="value"
                            options={getZones}
                            onBlur={() => handleInputBlur("zone")}
                            error={errors.zone?.message?.toString()}
                            register={register("zone", { required: "Zone is required" })}
                            required
                        />

                        <SelectInput
                            label="Region"
                            displayName="label"
                            valueName="value"
                            options={getRegions}
                            onBlur={() => handleInputBlur("region")}
                            error={errors.region?.message?.toString()}
                            register={register("region", { required: "Region is required" })}
                            required
                        />

                        <SelectInput
                            label="District"
                            displayName="label"
                            valueName="value"
                            options={getDistricts(selectedRegion)}
                            onBlur={() => handleInputBlur("district")}
                            error={errors.district?.message?.toString()}
                            register={register("district", { required: "District is required" })}
                            required
                        />

                        <SelectInput
                            label="Municipal"
                            displayName="label"
                            valueName="value"
                            options={getMunicipals(selectedRegion, selectedDistrict)}
                            onBlur={() => handleInputBlur("municipal")}
                            error={errors.municipal?.message?.toString()}
                            register={register("municipal", { required: "Municipal is required" })}
                            required
                        />

                        <TextInput
                            label="Street"
                            placeholder="Enter street"
                            onBlur={() => handleInputBlur("street")}
                            error={errors.street?.message?.toString()}
                            register={register("street")}
                            className="placeholder:font-extralight"
                        />

                        <TextInput
                        label="Post Code"
                        placeholder="Enter post code"
                        onBlur={() => handleInputBlur("principalBusinessLocationPostCode")}
                        error={errors.principalBusinessLocationPostCode?.message?.toString()}
                        register={register("principalBusinessLocationPostCode")}
                        className="placeholder:font-extralight "
                    />

                    <TextInput
                        label="P.O. Box"
                        placeholder="Enter P.O. Box"
                        onBlur={() => handleInputBlur("principalBusinessLocationPoBox")}
                        error={errors.principalBusinessLocationPoBox?.message?.toString()}
                        register={register("principalBusinessLocationPoBox")}
                        className="placeholder:font-extralight "
                    />

                    <TextInput
                        label="Email Address"
                        placeholder="Enter email address"
                        onBlur={() => handleInputBlur("principalBusinessLocationEmail")}
                        error={errors.principalBusinessLocationEmail?.message?.toString()}
                        register={register("principalBusinessLocationEmail")}
                        className="placeholder:font-extralight "
                    />

                    <PhoneNumberInput
                        name="principalBusinessLocationPhoneNumber"
                        label="Phone Number"
                        placeholder="Enter your phoneNumber"

                        control={control}
                        required
                        className="w-full"
                    />


                    <TextInput
                        label="Website"
                        placeholder="Enter your website URL link"
                        onBlur={() => handleInputBlur("principalBusinessWebsite")}
                        error={errors.principalBusinessWebsite?.message?.toString()}
                        register={register("principalBusinessWebsite")}
                        className="placeholder:font-extralight "
                    />


                </div>
            </div>
        </div>
    )
}